<template>
  <div class="p-3">
    <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>

    <div class="loading" v-if="fetchData">
      <load-data></load-data>
    </div>

    <div v-else>
      <div v-if="!qpSets.length" class="text-muted small py-2">
        No question paper sets assigned to you.
      </div>

      <table v-else class="table table-striped table-bordered">
        <thead class="thead-dark">
          <tr>
            <th>Exam</th>
            <th>Course</th>
            <th style="width:90px">Set</th>
            <th style="width:120px">Status</th>
            <th style="width:180px">Question Paper</th>
            <th style="width:120px"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="qp in qpSets" :key="qpKey(qp)">
            <td>{{ qp.exam_name }}</td>
            <td>{{ qp.course_code }} - {{ qp.course_name }}</td>
            <td>Set {{ qp.set }}</td>
            <td>
              <b-badge v-if="qp.submitted_at" variant="success">Submitted</b-badge>
              <b-badge v-else variant="secondary">Not Submitted</b-badge>
            </td>
            <td>
              <b-button
                size="sm"
                variant="primary"
                :disabled="openingKey === qpKey(qp)"
                @click="openQuestionPaper(qp)"
              >
                <b-spinner small v-if="openingKey === qpKey(qp)" class="mr-1" />
                {{ qp.question_paper_exam_id ? 'Open Question Paper' : 'Create Question Paper' }}
              </b-button>
            </td>
            <td>
              <b-button
                v-if="qp.question_paper_exam_id && !qp.submitted_at"
                size="sm"
                variant="outline-success"
                :disabled="submittingKey === qpKey(qp)"
                @click="submitQuestionPaper(qp)"
              >
                <b-spinner small v-if="submittingKey === qpKey(qp)" class="mr-1" />
                Submit
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Manage Exam Questions -->
    <DashboardModal
      id="questionPaperModal"
      parent="Question Paper"
      @close="closeQuestionPaperModal"
    >
    <ManageExamQuestions
      lazy
      v-if="selectedExam"
      :exam="selectedExam"
      :programme_config_group_id="selectedProgrammeConfigGroupId"
      :readOnly="false"
      :disableDelete="selectedSubmittedAt"
      :openFromExamController="true"
    />
    </DashboardModal>

  </div>
</template>

<script>
import DashboardModal from "../DashboardModal.vue";
import ManageExamQuestions from "../../examination/manageExamQuestions.vue";
export default {
  name: "FacultyCoeQpSets",

  components: {
      DashboardModal,
      ManageExamQuestions
  },
  data() {
    return {
      fetchData: false,
      errorMessage: "",
      qpSets: [],
      openingKey: null,
      submittingKey: null,

      // Data passed to ManageExamQuestions
      selectedExam: null,
      selectedProgrammeConfigGroupId: null,
      selectedSubmittedAt: false,
    };
  },

  async mounted() {
    await this.fetchQpSets();
  },

  methods: {
    qpKey(qp) {
      return `${qp.end_sem_exam_id}-${qp.grouped_course_id}-${qp.set}`;
    },

    async fetchQpSets() {
      this.fetchData = true;
      this.errorMessage = "";
      try {
        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        const res = await this.$axios.get(`${mainAPI}/faculty/qp-sets`);
        this.qpSets = res.data.data || [];
      } catch {
        this.errorMessage = "Failed to load your assigned question paper sets.";
      } finally {
        this.fetchData = false;
      }
    },

    async openQuestionPaper(qp) {
      this.errorMessage = "";
      const key = this.qpKey(qp);
      this.openingKey = key;
      try {
        let examId = qp.question_paper_exam_id;
        if (!examId) {
          const mainAPI = this.$store.getters.getAPIKey.mainAPI;
          const res = await this.$axios.post(
            `${mainAPI}/faculty/qp-sets/${qp.end_sem_exam_id}/${qp.grouped_course_id}/${qp.set}/question-paper`
          );
          examId = res.data.data.exam_id;
          const target = this.qpSets.find(item => this.qpKey(item) === key);
          if (target) target.question_paper_exam_id = examId;
        }
        await this.openManageExamQuestions(qp, examId);
      } catch {
        this.errorMessage = "Failed to open question paper.";
      } finally {
        this.openingKey = null;
      }
    },

    async submitQuestionPaper(qp) {
      this.errorMessage = "";
      const confirmed = await this.$bvModal.msgBoxConfirm(
        "Once submitted, this question paper can no longer be edited. Continue?",
        { title: "Submit Question Paper", okTitle: "Submit", cancelTitle: "Cancel", centered: true }
      );
      if (!confirmed) return;

      const key = this.qpKey(qp);
      this.submittingKey = key;
      try {
        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        const res = await this.$axios.post(
          `${mainAPI}/faculty/qp-sets/${qp.end_sem_exam_id}/${qp.grouped_course_id}/${qp.set}/submit`
        );
        const target = this.qpSets.find(item => this.qpKey(item) === key);
        if (target) target.submitted_at = res.data.data.submitted_at;
        this.$toast.success("Question paper submitted.", { duration: 3000 });
      } catch {
        this.errorMessage = "Failed to submit question paper.";
      } finally {
        this.submittingKey = null;
      }
    },

    async openManageExamQuestions(qp, examId) {
      const examination = this.$store.getters.getAPIKey.examination;
      const res = await this.$axios.get(`${examination}/${examId}`);
      const exam = res.data;
      exam.course_id = exam.course && exam.course.length ? exam.course[0].id : qp.course_id;
      exam.usergroup_id = qp.usergroup_id;
      exam.config_group_id = exam.config_group_id || qp.config_group_id;
      this.selectedExam = exam;
      this.selectedProgrammeConfigGroupId = qp.config_group_id;
      this.selectedSubmittedAt = !!qp.submitted_at;
      this.$bvModal.show("questionPaperModal");
    },

    closeQuestionPaperModal() { 
      this.$bvModal.hide("questionPaperModal");
      this.selectedExam = null;
      this.selectedProgrammeConfigGroupId = null;
    },
  },
};
</script>
